import { useAuth0 } from '@auth0/auth0-react'
import { ReactNode, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom-v5-compat'

import { getCurrentEnvironmentName } from '@sparkai/env'

import { getReturnToUrl } from '../utils/getReturnToUrl'
import {
  getSsoConnectionLoginOptions,
  isJdOktaConnectionRemembered,
  isJdOktaConnectionRequested,
} from '../utils/ssoConnection'
import { LoadingMessage } from './LoadingMessage'

export interface RedirectPageProps {
  children?: ReactNode
}

export function RedirectPage(_: RedirectPageProps) {
  const { error, loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
  const location = useLocation()

  const errorDescription = new URLSearchParams(location.search).get('error_description')
  const isJdOktaRequested = isJdOktaConnectionRequested(location.search)

  const retryLogin = useCallback(async () => {
    await loginWithRedirect({
      prompt: 'login',
      appState: { returnTo: getReturnToUrl() },
      'ext-is-sandbox': isOnAnSandboxEnvironment ? 'true' : 'false',
      ...getSsoConnectionLoginOptions(isJdOktaRequested || isJdOktaConnectionRemembered()),
    })
  }, [loginWithRedirect, isJdOktaRequested])

  /**
   * This prevents a dead end in the login flow
   * caused by having a password reset before the login.
   */
  useEffect(() => {
    if ((!isAuthenticated || isJdOktaRequested) && !isLoading && !error) {
      retryLogin()
    }
  }, [error, isAuthenticated, isJdOktaRequested, isLoading, retryLogin])

  if (error)
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center bg-gray-50'>
        <h1 className='font-inter text-2xl font-bold text-gray-900'>Failed to authenticate</h1>

        <p>{errorDescription ? errorDescription : 'Something went wrong when authenticating'}</p>
        <div className='mt-4'>
          <Button onClick={retryLogin}>Retry Login</Button>
        </div>
      </div>
    )

  return <LoadingMessage />
}

const Button = ({ children, onClick }: { children: ReactNode; onClick?: () => void }) => {
  return (
    <button
      onClick={onClick}
      type='button'
      className='px-auto py-auto inline-flex min-w-[94px] items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-inter text-sm font-medium text-white shadow-xs hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-blue-300'
    >
      <span className='mx-auto w-full'>{children}</span>
    </button>
  )
}

const isOnAnSandboxEnvironment = getCurrentEnvironmentName().includes('sandbox')
